import { Button } from '@ver-uds/react';
import { Alert } from '@ver-uds/uswds-react';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import ClientMouWrapper from '../../components/ClientMou/ClientMouWrapper';
import ErrorConstants from '../../constants/ErrorConstants';
import { useSignedMouDocumentQuery } from '../../services/MOUSignatureApi';

export type ClientMouResultsPageState =
  | null
  | undefined
  | {
      code: string;
    };

export enum MouSignType {
  SIGNED = 'SIGNED',
  REJECTED = 'REJECTED',
}
export interface ClientMouResultPageProps {
  mouSignType: MouSignType;
}

const ClientMouResultPage = ({ mouSignType }: ClientMouResultPageProps): JSX.Element => {
  const { state } = useLocation<ClientMouResultsPageState>();
  const otc = state?.code;
  const [loadingMouError, setLoadingMouError] = useState<boolean>(false);

  const { refetch } = useSignedMouDocumentQuery(otc || '', {
    enabled: false,
    useErrorBoundary: false,
    onError: (e) => {
      setLoadingMouError(true);
    },
  });

  const isAccepted = mouSignType === MouSignType.SIGNED;

  const downloadMou = async () => {
    const blobData = await refetch({ throwOnError: false });

    if (blobData && 'msSaveOrOpenBlob' in navigator && navigator.appVersion.toString().indexOf('.NET') > 0) {
      setLoadingMouError(false);
      navigator.msSaveOrOpenBlob(blobData, 'MouSignedDocument.pdf');
    } else if (blobData) {
      setLoadingMouError(false);
      const url = URL.createObjectURL(blobData);
      window.open(url, '_blank');
    }
  };

  return (
    <ClientMouWrapper title="Enrollment Completed" header="Completing the Client Enrollment Process">
      <section className="padding-y-4 grid-col-12">
        <h1 className="padding-y-1">{isAccepted ? 'Enrollment Completed' : 'MOU Declined'}</h1>
        <p className="usa-intro">
          {isAccepted
            ? "You've electronically signed the Memorandum of Understanding (MOU). We'll notify the Employer Agent who requested your approval for the MOU."
            : "You've declined to electronically sign the Memorandum of Understanding (MOU). We'll notify the Employer Agent who requested your approval for the MOU."}
        </p>
        <Alert status={isAccepted ? 'success' : 'info'}>
          {isAccepted ? <Alert.Heading>You&apos;re all set!</Alert.Heading> : null}
          <Alert.Text>
            {isAccepted
              ? "You're all set! Your signed MOU is now available for download. Thank you, and welcome to E-Verify!"
              : "If you've declined the MOU in error, you may ask your agent to resubmit the MOU to DHS again."}
          </Alert.Text>
        </Alert>
        {isAccepted && (
          <Button variant="secondary" type="button" className="margin-top-3" onClick={downloadMou}>
            Download Memorandum of Understanding
          </Button>
        )}
        {loadingMouError ? (
          <Alert status="error" className="margin-top-3" slim>
            <Alert.Text>{ErrorConstants.DOWNLOAD_MOU_ERROR}</Alert.Text>
          </Alert>
        ) : null}
      </section>
    </ClientMouWrapper>
  );
};

export default ClientMouResultPage;
